/**
 * AdminSystemPrompts Page
 * CRUD interface for managing system AI prompts.
 * Admin-only — requires Cognito 'admins' group membership.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Shield,
  ShieldAlert,
  ArrowLeft,
  Sparkles,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import { PageLayout } from '@/components';
import { Button, UnauthenticatedPage } from '@/components/ui';
import { systemPromptsApi, SystemPrompt, SaveSystemPromptRequest } from '@/services/systemPromptsApi';
import { errorMessages } from '@/lib/errors';
import { formatDate as formatDateLocale } from '@/lib/i18n-utils';

// Category options — labels are i18n keys resolved at render time
const CATEGORY_OPTIONS = [
  { value: 'documentation', labelKey: 'admin.prompts.categoryDocumentation', bg: 'bg-blue-100', text: 'text-blue-700' },
  { value: 'analysis', labelKey: 'admin.prompts.categoryAnalysis', bg: 'bg-purple-100', text: 'text-purple-700' },
  { value: 'processing', labelKey: 'admin.prompts.categoryProcessing', bg: 'bg-amber-100', text: 'text-amber-700' },
];

const getCategoryClasses = (category: string) => {
  const found = CATEGORY_OPTIONS.find(c => c.value === category);
  return { bg: found?.bg || 'bg-slate-100', text: found?.text || 'text-slate-700' };
};

interface EditorState {
  isOpen: boolean;
  mode: 'create' | 'edit';
  promptId: string;
  promptName: string;
  category: string;
  description: string;
  template: string;
  modelId: string;
  maxTokens: number;
  anthropicVersion: string;
  version: string;
  isActive: boolean;
  metadataJson: string;
  hasUnsavedChanges: boolean;
  originalData: string;
}

const initialEditorState: EditorState = {
  isOpen: false,
  mode: 'create',
  promptId: '',
  promptName: '',
  category: 'documentation',
  description: '',
  template: '',
  modelId: 'anthropic.claude-3-sonnet-20240229-v1:0',
  maxTokens: 4096,
  anthropicVersion: 'bedrock-2023-05-31',
  version: '1.0.0',
  isActive: true,
  metadataJson: '{}',
  hasUnsavedChanges: false,
  originalData: '',
};

export const AdminSystemPrompts: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: authLoading, isAdmin } = useAuthContext();
  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>(initialEditorState);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (actionError) {
      const timer = setTimeout(() => setActionError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [actionError]);

  const fetchPrompts = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const data = await systemPromptsApi.listSystemPrompts();
      setPrompts(data);
    } catch (err) {
      setError(errorMessages.loadSystemPrompts(err));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchPrompts();
    }
  }, [isAuthenticated, isAdmin, fetchPrompts]);

  const formatDate = (dateString: string) => {
    try {
      return formatDateLocale(dateString, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const handleCreate = () => {
    setEditor({
      ...initialEditorState,
      isOpen: true,
      mode: 'create',
    });
    setActionError(null);
  };

  const handleEdit = async (prompt: SystemPrompt) => {
    setActionError(null);
    try {
      const full = await systemPromptsApi.getSystemPrompt(prompt.promptId);
      const dataSnapshot = JSON.stringify({
        promptName: full.promptName,
        category: full.category,
        description: full.description,
        template: full.template,
        modelId: full.modelId,
        maxTokens: full.maxTokens,
        version: full.version,
        isActive: full.isActive,
        metadata: full.metadata,
      });
      setEditor({
        isOpen: true,
        mode: 'edit',
        promptId: full.promptId,
        promptName: full.promptName,
        category: full.category,
        description: full.description || '',
        template: full.template || '',
        modelId: full.modelId || 'anthropic.claude-3-sonnet-20240229-v1:0',
        maxTokens: full.maxTokens || 4096,
        anthropicVersion: full.anthropicVersion || 'bedrock-2023-05-31',
        version: full.version || '1.0.0',
        isActive: full.isActive ?? true,
        metadataJson: JSON.stringify(full.metadata || {}, null, 2),
        hasUnsavedChanges: false,
        originalData: dataSnapshot,
      });
    } catch (err) {
      setActionError(errorMessages.loadSystemPrompts(err));
    }
  };

  const handleCloseEditor = useCallback(() => {
    if (editor.hasUnsavedChanges) {
      if (!window.confirm(t('admin.prompts.unsavedConfirm'))) {
        return;
      }
    }
    setEditor(initialEditorState);
  }, [editor.hasUnsavedChanges, t]);

  const handleSave = async () => {
    if (!editor.promptName.trim()) {
      setActionError(t('admin.prompts.validationPromptName'));
      return;
    }
    if (!editor.template.trim()) {
      setActionError(t('admin.prompts.validationPromptTemplate'));
      return;
    }
    if (editor.mode === 'create' && !editor.promptId.trim()) {
      setActionError(t('admin.prompts.validationPromptId'));
      return;
    }

    // Validate metadata JSON
    let metadata: Record<string, unknown> = {};
    try {
      metadata = JSON.parse(editor.metadataJson || '{}');
    } catch {
      setActionError(t('admin.prompts.validationMetadataJson'));
      return;
    }

    setIsSaving(true);
    setActionError(null);

    try {
      const request: SaveSystemPromptRequest = {
        promptName: editor.promptName.trim(),
        category: editor.category,
        description: editor.description.trim(),
        template: editor.template,
        modelId: editor.modelId.trim(),
        maxTokens: editor.maxTokens,
        anthropicVersion: editor.anthropicVersion.trim(),
        version: editor.version.trim(),
        isActive: editor.isActive,
        metadata,
      };

      if (editor.mode === 'create') {
        request.promptId = editor.promptId.trim();
        await systemPromptsApi.createSystemPrompt(request);
      } else {
        await systemPromptsApi.updateSystemPrompt(editor.promptId, request);
      }

      setEditor(initialEditorState);
      await fetchPrompts();
    } catch (err) {
      setActionError(errorMessages.saveSystemPrompt(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (promptId: string) => {
    setActionError(null);
    try {
      await systemPromptsApi.deleteSystemPrompt(promptId);
      setDeleteConfirm(null);
      await fetchPrompts();
    } catch (err) {
      setActionError(errorMessages.deleteSystemPrompt(err));
    }
  };

  // Group prompts by category
  const groupedPrompts = CATEGORY_OPTIONS.map(cat => ({
    ...cat,
    prompts: prompts.filter(p => p.category === cat.value),
  })).filter(group => group.prompts.length > 0);

  if (authLoading) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <UnauthenticatedPage
        icon={<Shield className="w-8 h-8 text-slate-400" />}
        title={t('admin.prompts.unauthTitle')}
        description={t('admin.prompts.unauthDescription')}
      />
    );
  }

  if (!isAdmin) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="text-center py-16">
            <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('admin.prompts.accessDenied')}</h1>
            <p className="text-slate-600 mb-6">{t('admin.prompts.noPermission')}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors"
            >
              {t('admin.prompts.goHome')}
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (isLoading && prompts.length === 0) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mr-3" />
            <span className="text-slate-600">{t('admin.prompts.loadingPrompts')}</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link
                to="/admin"
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                title={t('admin.prompts.backToAdmin')}
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Sparkles className="w-6 h-6 text-indigo-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">{t('admin.prompts.title')}</h1>
            </div>
            <p className="text-slate-600 ml-[88px]">
              {t('admin.prompts.promptCount', { count: prompts.length })}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchPrompts(true)}
              disabled={isRefreshing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {t('admin.prompts.refresh')}
            </button>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('admin.prompts.newPrompt')}
            </button>
          </div>
        </div>

        {/* Error banner */}
        {(error || actionError) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-red-700">{actionError || error}</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && prompts.length === 0 && (
          <div className="text-center py-16 bg-slate-50 rounded-2xl border border-slate-200">
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">{t('admin.prompts.noPromptsTitle')}</h2>
            <p className="text-slate-500 mb-6">
              {t('admin.prompts.noPromptsDescription')}
            </p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('admin.prompts.createPrompt')}
            </button>
          </div>
        )}

        {/* Prompts grouped by category */}
        {groupedPrompts.map(group => (
          <div key={group.value} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${group.bg} ${group.text}`}>
                {t(group.labelKey)}
              </span>
              <span className="text-sm text-slate-400">{t('admin.prompts.promptCountInGroup', { count: group.prompts.length })}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {group.prompts.map(prompt => (
                <div
                  key={prompt.promptId}
                  className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{prompt.promptName}</h3>
                      <p className="text-xs text-slate-400 font-mono">{prompt.promptId}</p>
                    </div>
                    {prompt.isActive ? (
                      <ToggleRight className="w-5 h-5 text-emerald-500 shrink-0 ml-2" />
                    ) : (
                      <ToggleLeft className="w-5 h-5 text-slate-300 shrink-0 ml-2" />
                    )}
                  </div>

                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                    {prompt.description || t('admin.prompts.noDescription')}
                  </p>

                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                      v{prompt.version}
                    </span>
                    <span className="text-xs text-slate-400 truncate" title={prompt.modelId}>
                      {prompt.modelId?.split('/').pop() || prompt.modelId}
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-400">
                      {t('admin.prompts.updatedDate', { date: formatDate(prompt.updatedAt) })}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(prompt)}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title={t('admin.prompts.editButton')}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(prompt.promptId)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('admin.prompts.deleteButton')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('admin.prompts.deleteModalTitle')}</h3>
              <p className="text-slate-600 mb-6">
                {t('admin.prompts.deleteModalDescription')}
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  {t('admin.prompts.cancel')}
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t('admin.prompts.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Editor modal */}
        {editor.isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
            <div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={handleCloseEditor}
            />
            <div className="relative bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:rounded-2xl shadow-2xl flex flex-col">
              {/* Modal header */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base sm:text-xl font-bold text-slate-900">
                        {editor.mode === 'create' ? t('admin.prompts.editorCreateTitle') : editor.promptName || t('admin.prompts.editorEditTitle')}
                      </h3>
                      {editor.hasUnsavedChanges && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          {t('admin.prompts.unsaved')}
                        </span>
                      )}
                    </div>
                    {editor.mode === 'edit' && (
                      <p className="text-xs sm:text-sm text-slate-500 mt-0.5 font-mono">{editor.promptId}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleCloseEditor}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal body */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
                {/* Row 1: Prompt ID and Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t('admin.prompts.labelPromptId')}
                    </label>
                    <input
                      type="text"
                      value={editor.promptId}
                      onChange={e => setEditor(prev => ({ ...prev, promptId: e.target.value, hasUnsavedChanges: true }))}
                      placeholder={t('admin.prompts.placeholderPromptId')}
                      disabled={editor.mode === 'edit'}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm disabled:bg-slate-50 disabled:text-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t('admin.prompts.labelPromptName')}
                    </label>
                    <input
                      type="text"
                      value={editor.promptName}
                      onChange={e => setEditor(prev => ({ ...prev, promptName: e.target.value, hasUnsavedChanges: true }))}
                      placeholder={t('admin.prompts.placeholderPromptName')}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                {/* Row 2: Category, Version, Active */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.prompts.labelCategory')}</label>
                    <select
                      value={editor.category}
                      onChange={e => setEditor(prev => ({ ...prev, category: e.target.value, hasUnsavedChanges: true }))}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {CATEGORY_OPTIONS.map(cat => (
                        <option key={cat.value} value={cat.value}>{t(cat.labelKey)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.prompts.labelVersion')}</label>
                    <input
                      type="text"
                      value={editor.version}
                      onChange={e => setEditor(prev => ({ ...prev, version: e.target.value, hasUnsavedChanges: true }))}
                      placeholder="1.0.0"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editor.isActive}
                        onChange={e => setEditor(prev => ({ ...prev, isActive: e.target.checked, hasUnsavedChanges: true }))}
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-slate-700">{t('admin.prompts.labelActive')}</span>
                    </label>
                  </div>
                </div>

                {/* Row 3: Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.prompts.labelDescription')}</label>
                  <input
                    type="text"
                    value={editor.description}
                    onChange={e => setEditor(prev => ({ ...prev, description: e.target.value, hasUnsavedChanges: true }))}
                    placeholder={t('admin.prompts.placeholderDescription')}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Row 4: Template (the main prompt text) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('admin.prompts.labelPromptTemplate')}
                  </label>
                  <textarea
                    value={editor.template}
                    onChange={e => setEditor(prev => ({ ...prev, template: e.target.value, hasUnsavedChanges: true }))}
                    placeholder={t('admin.prompts.placeholderPromptTemplate')}
                    rows={12}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {t('admin.prompts.promptTemplateHint')}
                  </p>
                </div>

                {/* Row 5: Model config */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.prompts.labelModelId')}</label>
                    <input
                      type="text"
                      value={editor.modelId}
                      onChange={e => setEditor(prev => ({ ...prev, modelId: e.target.value, hasUnsavedChanges: true }))}
                      placeholder="anthropic.claude-3-sonnet-20240229-v1:0"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.prompts.labelMaxTokens')}</label>
                    <input
                      type="number"
                      value={editor.maxTokens}
                      onChange={e => setEditor(prev => ({ ...prev, maxTokens: parseInt(e.target.value) || 4096, hasUnsavedChanges: true }))}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.prompts.labelAnthropicVersion')}</label>
                    <input
                      type="text"
                      value={editor.anthropicVersion}
                      onChange={e => setEditor(prev => ({ ...prev, anthropicVersion: e.target.value, hasUnsavedChanges: true }))}
                      placeholder="bedrock-2023-05-31"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Row 6: Metadata JSON */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.prompts.labelMetadata')}</label>
                  <textarea
                    value={editor.metadataJson}
                    onChange={e => setEditor(prev => ({ ...prev, metadataJson: e.target.value, hasUnsavedChanges: true }))}
                    placeholder={t('admin.prompts.placeholderMetadata')}
                    rows={3}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {t('admin.prompts.metadataHint')}
                  </p>
                </div>
              </div>

              {/* Modal footer */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-slate-50 sm:rounded-b-2xl">
                <button
                  onClick={handleCloseEditor}
                  disabled={isSaving}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  {editor.hasUnsavedChanges ? t('admin.prompts.discard') : t('admin.prompts.cancel')}
                </button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !editor.promptName.trim() || !editor.template.trim()}
                  variant="gradient-emerald"
                  size="md"
                  shape="default"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('admin.prompts.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editor.mode === 'create' ? t('admin.prompts.createPrompt') : t('admin.prompts.saveChanges')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default AdminSystemPrompts;
